'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';

import { cn } from '../lib/cn';
import { DaemonManagerPanel } from './DaemonManageModal';
import { MemberRunnerModelManager } from './MemberRunnerModelManager';
import { useTeams } from '../hooks/queries/useTeams';
import { useWorkspaceScope } from '../hooks/useWorkspaceScope';
import { executionEnvironmentSections, type ExecutionEnvironmentSection } from '../lib/executionEnvironmentModal';

type ExecutionEnvironmentViewProps = {
  /** 진입 시 열어 둘 섹션. 기존 `/member-runner-models` 북마크는 모델 섹션으로 진입한다. */
  initialSection?: ExecutionEnvironmentSection;
};

/**
 * 사용자 계정 범위의 실행 환경 허브 본문.
 * 러너 등록·지원 엔진·프로젝트 사용 권한(공유)과 개인 러너 모델을 한 곳에서 관리한다.
 * 프로젝트 범위 설정(에이전트 연결, 실행 제약, 공유 노출)은 `/agent-configs`가 담당한다.
 *
 * 계정 자산이 프로젝트에 종속된 것처럼 읽히지 않도록 프로젝트 사이드바 셸의 페이지가 아니라
 * 전역 모달(`ExecutionEnvironmentModal`)로 띄운다. 제목·설명은 모달 헤더가 가진다.
 */
export const ExecutionEnvironmentView = ({ initialSection = 'runners' }: ExecutionEnvironmentViewProps) => {
  const t = useTranslations('executionEnvironment');
  // 계정 범위 화면이므로 프로젝트 선택은 진입 조건이 아니다. teamId는 공유 대상 기본값으로만 쓴다.
  const { token, teamId } = useWorkspaceScope();
  const { data: teams = [] } = useTeams(token);
  const [section, setSection] = useState<ExecutionEnvironmentSection>(initialSection);

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <div role="tablist" aria-label={t('title')} className="flex min-w-0 shrink-0 flex-wrap gap-1">
        {executionEnvironmentSections.map((value) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={section === value}
            className={cn(
              'rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
              section === value
                ? 'bg-primary/10 text-primary'
                : 'text-[var(--color-fg-muted)] hover:bg-base-200 hover:text-base-content',
            )}
            onClick={() => setSection(value)}
          >
            {value === 'runners' ? t('sectionRunners') : t('sectionModels')}
          </button>
        ))}
      </div>

      <section
        className={cn('mt-4 min-h-0 min-w-0 flex-1', section === 'runners' ? 'overflow-y-auto pr-1' : 'flex flex-col')}
        aria-label={section === 'runners' ? t('sectionRunners') : t('sectionModels')}
      >
        <p className="mb-3 shrink-0 text-xs leading-relaxed text-[var(--color-fg-muted)]">
          {section === 'runners' ? t('runnersDescription') : t('modelsDescription')}
        </p>
        {section === 'runners' ? (
          <DaemonManagerPanel token={token} teams={teams} currentTeamId={teamId} />
        ) : (
          <MemberRunnerModelManager token={token} />
        )}
      </section>
    </div>
  );
};
